/**
 * Unit members management
 */

import { supabase } from '../supabase'
import type { UnitMember, Role } from '../types'
import { getAuthUser, PROFILES_USER_ID_COL } from './helpers'
import { validateCapability } from '../services/permissions'

/**
 * Add member to unit
 */
export async function addUnitMember(
  unitId: string,
  userId: string,
  role: Role,
  userRole?: Role
): Promise<UnitMember> {
  if (userRole) {
    await validateCapability(userRole, 'canManageTeam')
  }
  const { data, error } = await supabase
    .from('unit_members')
    .insert({
      unit_id: unitId,
      user_id: userId,
      role
    })
    .select()
    .single()

  if (error) throw error
  return data as UnitMember
}

/**
 * Update unit member role
 */
export async function updateUnitMemberRole(memberId: string, role: Role, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canManageTeam')
  }
  const { error } = await supabase
    .from('unit_members')
    .update({ role })
    .eq('id', memberId)

  if (error) throw error
}

/**
 * Remove member from unit
 */
export async function removeUnitMember(memberId: string, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canManageTeam')
  }
  const { error } = await supabase
    .from('unit_members')
    .delete()
    .eq('id', memberId)

  if (error) throw error
}

/**
 * Get unit members
 * - Prefers RPC get_unit_members_with_profiles
 * - If RPC fails OR returns empty, falls back to:
 *   - unit_members
 *   - profiles
 *   - plus current auth user email/name as fallback when profile is missing
 */
export async function getUnitMembers(unitId: string): Promise<any[]> {
  console.log('[getUnitMembers] Fetching members for unit:', unitId)

  const authUser = await getAuthUser()

  // 1) Try RPC
  const { data: rpcData, error: rpcError } = await supabase
    .rpc('get_unit_members_with_profiles', { p_unit_id: unitId })

  if (!rpcError && Array.isArray(rpcData) && rpcData.length > 0) {
    return rpcData
  }

  if (rpcError) {
    console.warn('[getUnitMembers] RPC failed, using fallback:', rpcError)
  } else {
    console.warn('[getUnitMembers] RPC returned empty, using fallback')
  }

  // 2) Fallback: unit_members
  const { data: membersData, error: membersError } = await supabase
    .from('unit_members')
    .select(`
      id,
      unit_id,
      user_id,
      role,
      created_at
    `)
    .eq('unit_id', unitId)
    .order('created_at', { ascending: true })

  if (membersError) throw membersError

  const memberUserIds = (membersData || []).map((m: any) => m.user_id)
  if (memberUserIds.length === 0) return []

  // 3) Fallback: profiles for these users
  const { data: profilesData, error: profilesError } = await supabase
    .from('profiles')
    .select(`${PROFILES_USER_ID_COL}, full_name, email, phone`)
    .in(PROFILES_USER_ID_COL, memberUserIds)

  if (profilesError) {
    console.warn('[getUnitMembers] Could not load profiles (ok, will fallback):', profilesError)
  }

  const profilesMap = new Map(
    (profilesData || []).map((p: any) => [p[PROFILES_USER_ID_COL], p])
  )

  // 4) Merge
  const merged = (membersData || []).map((m: any) => {
    const profile = profilesMap.get(m.user_id) as any

    const isCurrentUser = authUser?.id === m.user_id
    const fallbackEmail = isCurrentUser ? authUser?.email ?? null : null
    const fallbackName = isCurrentUser ? (authUser?.user_metadata?.full_name ?? null) : null

    return {
      id: m.id,
      unit_id: m.unit_id,
      user_id: m.user_id,
      role: m.role,
      created_at: m.created_at,
      full_name: profile?.full_name ?? fallbackName,
      email: profile?.email ?? fallbackEmail,
      phone: profile?.phone ?? null
    }
  })

  console.log('[getUnitMembers] Fallback merged result:', merged)
  return merged
}
